/**
 * Lightweight browser-side execution timer.
 *
 * Logs a summary to the console when an execution completes.
 * Enable via VITE_EXECUTION_TRACE=1 in .env or
 * window.__DENKER_TRACE__ = true in the browser console.
 */

import {
  recordSessionLatency,
  sessionLatencyEnabled,
} from "./session-latency-trace";

interface Trace {
  checkpoints: Map<string, number>;
  counters: Map<string, number>;
  createdAt: number;
}

const traces = new Map<string, Trace>();
const TTL_MS = 300_000; // 5 min

function isEnabled(): boolean {
  return sessionLatencyEnabled();
}

function getOrCreate(executionId: string): Trace {
  let trace = traces.get(executionId);
  if (!trace) {
    trace = {
      checkpoints: new Map(),
      counters: new Map(),
      createdAt: performance.now(),
    };
    traces.set(executionId, trace);
  }
  return trace;
}

export function checkpoint(
  executionId: string,
  name: string,
  once = false,
): void {
  if (!isEnabled()) return;
  const trace = getOrCreate(executionId);
  if (once && trace.checkpoints.has(name)) return;
  trace.checkpoints.set(name, performance.now());
  recordSessionLatency(`execution.${name}`, { executionId });
}

export function increment(executionId: string, name: string, amount = 1): void {
  if (!isEnabled()) return;
  const trace = traces.get(executionId);
  if (!trace) return;
  trace.counters.set(name, (trace.counters.get(name) ?? 0) + amount);
}

/**
 * Transfer a trace from one key to another.
 *
 * Used to move a "_pending" trace to the real execution_id
 * once the server responds with execution.status running.
 */
export function rekey(oldId: string, newId: string): void {
  if (!isEnabled()) return;
  recordSessionLatency("execution.bound", {
    operationId: oldId,
    executionId: newId,
  });
  const trace = traces.get(oldId);
  if (trace) {
    traces.delete(oldId);
    traces.set(newId, trace);
  }
}

export function finish(executionId: string): void {
  if (!isEnabled()) return;
  const trace = traces.get(executionId);
  traces.delete(executionId);
  cleanup();
  if (!trace) return;

  const cp = trace.checkpoints;
  const base = cp.get("prompt_submitted") ?? trace.createdAt;

  const ordered = [
    "prompt_submitted",
    "first_ws_event",
    "skeleton_frame_rendered",
    "first_frame_rendered",
    "tts_reply_requested",
    "tts_final_message_received",
    "tts_sidecar_invoked",
    "execution_completed",
    "execution_paused",
  ];

  const lines: string[] = [
    `=== Execution Trace: ${executionId.slice(0, 8)}... ===`,
  ];
  for (const name of ordered) {
    const t = cp.get(name);
    if (t !== undefined) {
      lines.push(`  ${name}: +${Math.round(t - base)}ms`);
    }
  }
  for (const [k, v] of trace.counters) {
    lines.push(`  ${k}: ${v}`);
  }
  const total = cp.get("execution_completed") ?? cp.get("execution_paused");
  if (total !== undefined) {
    lines.push(`  total_browser_ms: ${Math.round(total - base)}ms`);
  }

  // eslint-disable-next-line no-console
  console.log(`%c${lines.join("\n")}`, "color: #30D158; font-weight: bold");
}

function cleanup(): void {
  const now = performance.now();
  for (const [id, t] of traces) {
    if (now - t.createdAt > TTL_MS) traces.delete(id);
  }
}
